import { useMemo, useState } from 'react'
import clsx from 'clsx'

import { TableAdapter } from '@makyoapp/makyo-common'
import { Search16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { CloseCircle, DropdownV2 } from '@makyoapp/makyo-ui'

import { GroupedTable } from '@/core/db'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useGetTablesGroup } from '@/hooks/useGetTablesGroup'
import { isEmpty } from '@/shared/utils'
import { TableIconMap } from '@/components/RenderTableIcon'

interface TablesDropdownV2Props {
  value: string | undefined
  onChange?: (value: string) => void
  tables?: TableAdapter[]
  showPlaceholder?: boolean
  triggerClassName?: string
  menuClassName?: string
  filterOption?: (items: TableAdapter[]) => TableAdapter[]
  showSourceTableViews?: boolean
  disabled?: boolean
}

/**
 * New tables dropdown component with grouped tables support
 */
export function TablesDropdownV2({
  value = '',
  onChange,
  tables = [],
  showPlaceholder = false,
  triggerClassName,
  menuClassName,
  filterOption,
  showSourceTableViews = false,
  disabled = false,
}: TablesDropdownV2Props) {
  const { tables: tableAdapters } = useDbClientContext()
  const [groupedTables] = useGetTablesGroup()

  const [show, setShow] = useState(false)
  const [search, setSearch] = useState('')

  const allTables = useMemo(() => {
    let options = isEmpty(tables) ? tableAdapters : tables
    if (filterOption) {
      options = filterOption(options)
    }
    return options
  }, [tableAdapters, tables, filterOption])

  const tableOptions: GroupedTable[] = useMemo(() => {
    const options = groupedTables.filter((item) => {
      return allTables.some((t) => t.id === item.id)
    })
    return showPlaceholder ? [{ id: '', name: 'Select Table', type: 'source' }, ...options] : options
  }, [groupedTables, showPlaceholder, allTables])

  const selectedTable = tableOptions.find((item) => item.id === value)

  const filteredTables = useMemo(() => {
    if (!search) return tableOptions

    const keyword = search.toLowerCase()

    const parents = tableOptions.filter((item) => !item.parent)
    const matchingParents = parents.filter((item) => item.name.toLowerCase().includes(keyword))
    const matchingChildren = tableOptions.filter((item) => item.parent && item.name.toLowerCase().includes(keyword))

    const result: GroupedTable[] = []
    for (const parent of parents) {
      const parentIsMatch = matchingParents.some((p) => p.id === parent.id)
      const childrenForParent = matchingChildren.filter((c) => c.parent === parent.id)
      if (parentIsMatch || childrenForParent.length > 0) {
        result.push(parent, ...childrenForParent)
      }
    }

    return result
  }, [tableOptions, search])

  const noValue = !selectedTable || selectedTable.id === ''

  return (
    <DropdownV2 open={show} onOpenChange={setShow}>
      <DropdownV2.Trigger
        asChild
        onClick={(e) => {
          if (disabled) {
            e.preventDefault()
          }
        }}
      >
        <div
          title={selectedTable?.name}
          className={clsx(
            'w-full h-fit min-h-10 px-4 py-2  flex items-center gap-2 flex-row transition-colors rounded truncate break-words ',
            'bg-zinc-200 dark:bg-slate-900',
            disabled ? 'cursor-not-allowed opacity-50 ' : 'cursor-pointer hover:bg-zinc-300 dark:hover:bg-slate-700',
            triggerClassName
          )}
        >
          <div className={clsx('flex-1 flex items-center gap-2 truncate', noValue && 'text-light')}>
            {selectedTable && TableIconMap[selectedTable.type]}
            <span className={clsx('txt', noValue && 'text-light')}>{selectedTable?.name}</span>
          </div>
          <MaterialIcon size={20} icon='keyboard_arrow_down' />
        </div>
      </DropdownV2.Trigger>

      <DropdownV2.Content className={clsx('z-302 text-m-m', menuClassName)} sideOffset={8}>
        <div className='w-full'>
          <div className='sdd-search-wrap'>
            <Search16 className='sdd-search-icon' />
            <input
              type='text'
              className='sdd-search-input'
              value={search}
              onChange={(e) => {
                setSearch?.(e.currentTarget.value)
              }}
            />
            <button
              type='button'
              className={clsx('sdd-search-remove', search ? 'visible' : 'invisible')}
              onClick={() => {
                setSearch?.('')
              }}
            >
              <CloseCircle />
            </button>
          </div>
        </div>

        <div className='max-h-[300px] overflow-y-auto overflow-x-hidden'>
          {filteredTables.map((item, index) => (
            <div
              key={`${item.id}-${index}`}
              className={clsx(
                'flex cursor-pointer items-center rounded-sm px-4 py-2 hover:bg-zinc-200 dark:hover:bg-slate-700',
                'select-none',
                item.parent && 'pl-9',
                value === item.id ? 'bg-zinc-100 dark:bg-slate-600' : '',
                item.id === '' && 'text-light'
              )}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onChange?.(item.id)
                setShow(false)
              }}
            >
              <div className='flex-1 flex items-center gap-x-2'>
                {TableIconMap[item.type]}
                <span className='flex-1 text-left'>{item.name}</span>
                {showSourceTableViews && !!item.childrenCount && item.childrenCount > 0 && (
                  <span className='text-m-sm text-light'>{item.childrenCount} views</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </DropdownV2.Content>
    </DropdownV2>
  )
}
